import { ref } from 'vue'
import { defineStore } from 'pinia'
import { isEqual } from 'lodash'
import { useToaster } from '@/modules/toaster/composables/use-toaster'
import { useFilterStore } from '@/modules/filter/stores/filter.store'
import { useSearchStore } from '@/modules/search/stores/search.store'
import { MotionTrackDefault, type MotionTrack } from '@/shared/interfaces/item'
import { motionViewApi } from '../api/motion.api'
import type { MBox, MBoxPriorityItem, OutboundShipmentsMonitorByIds } from '@/api/models/types'
import type { HttpResponse } from '@/services/http'
import type { Shipment, Validation } from '@/shared/models/shared.model'
import type { MotionStatusErrorCode } from '../types/motion.enums'
import type { EncryptedKeyValue, ExternalKey, MotionViewStatus } from '../models/motion.model'
import type { MotionBoxErrorCode } from '@/modules/motion-box/types/motion-box.enums'
import type { MotionBoxStatusRequest, NotificationRequest } from '../api/motion.requests'

export const useMotionStore = defineStore('motion', () => {
  const filterStore = useFilterStore()
  const searchStore = useSearchStore()
  const statuses = ref<MotionViewStatus[]>([])
  const lastKnownChangeVersion = ref<string>('0')
  const trackId = ref<number>(0)
  const items = ref<MBox[]>([])
  const priorityItems = ref<MBoxPriorityItem[]>([])
  const isLoading = ref({
    update: false,
    init: false,
  })
  const { success } = useToaster()

  const loadStatuses = async (): Promise<void> => {
    const { data, error } = await motionViewApi.getStatuses()
    if (!error && data) {
      statuses.value = data
    }
  }

  const getDimensions = async (
    motionBoxId: number,
  ): Promise<HttpResponse<Record<number, number>, MotionBoxErrorCode>> => {
    return await motionViewApi.getDimensions(motionBoxId)
  }

  const getStatusValidation = async (
    motionBoxIds: number[],
  ): Promise<HttpResponse<Validation | null, any>> => {
    return motionViewApi.getStatusValidation(motionBoxIds)
  }

  const getMotionBoxes = async (): Promise<void> => {
    const { data } = await motionViewApi.getBoxes(filterStore.filter)
    if (data && data.items && isEqual(data.status, filterStore.filter.status)) {
      items.value = data.items
      lastKnownChangeVersion.value = data.changeVersion
      trackId.value = data.trackId
      success('glossary.showingCount', { count: data.items.length })
    }
  }

  const getRelatedMotionBoxes = async (): Promise<void> => {
    if (searchStore.relatedId) {
      const item = items.value.find((item) => item.id === searchStore.relatedId)
      const { data } = await motionViewApi.getRelatedMotionBox(searchStore.relatedId)
      if (data) {
        items.value = data.priorityItems
      } else if (item) {
        items.value = [{ ...item }]
      }
    }
  }

  const refreshMotionItems = async (): Promise<void> => {
    isLoading.value.init = true
    if (searchStore.relatedId) {
      await getRelatedMotionBoxes()
    } else if (!searchStore.isSearchActive) {
      await getMotionBoxes()
    }
    isLoading.value.init = false
  }

  const getExternalKey = async (
    mBoxId: number,
  ): Promise<HttpResponse<ExternalKey | null, MotionBoxErrorCode>> => {
    return await motionViewApi.getExternalKey(mBoxId, false)
  }

  const getDeliveryNote = async (
    mBoxId: number,
  ): Promise<HttpResponse<EncryptedKeyValue | null, MotionBoxErrorCode>> => {
    return await motionViewApi.getDeliveryNote(mBoxId, false)
  }

  const getMotionSelectedItems = async (): Promise<void> => {
    isLoading.value.init = true
    const { data } = await motionViewApi.searchBoxes({ priorityItems: priorityItems.value })
    if (data && data.priorityItems) {
      items.value = data.priorityItems
      lastKnownChangeVersion.value = data.changeVersion
      trackId.value = data.trackId
    }
    isLoading.value.init = false
  }

  const firstItem = (): MotionTrack | null => {
    if (items.value.length >= 1) {
      return { id: items.value[0].id, index: 0, status: items.value[0].status }
    }
    return null
  }

  const getUpdatedStatus = (updatedItems: MBox[]): { [key: number]: MBox } => {
    return updatedItems.reduce((updatedState: { [key: number]: MBox }, updateItem: MBox) => {
      const index = items.value.findIndex((item: MBox) => item.id === updateItem.id)
      if (index > -1) {
        updatedState[index] = updateItem
      }
      return updatedState
    }, {})
  }

  const updateMotionItems = async (): Promise<{ success: boolean; error?: string }> => {
    const payload: OutboundShipmentsMonitorByIds = {
      fromViewDate: filterStore.filter.fromViewDate,
      toViewDate: filterStore.filter.toViewDate,
      status: filterStore.filter.status,
      fallbackQueuePriority: 15,
      priorityItems: priorityItems.value,
    }
    const { data, error } = await motionViewApi.getUpdatedBoxes(payload)
    if (error) {
      return { success: false, error }
    }
    if (data) {
      const updatedStates = getUpdatedStatus(data.priorityItems)
      for (const [key, value] of Object.entries(updatedStates)) {
        items.value[Number(key)].status = value.status
        items.value[Number(key)].flag = value.flag
        items.value[Number(key)].severity = value.severity
        items.value[Number(key)].mType = value.mType
        items.value[Number(key)].isHidden = value.isHidden
      }
    }
    return { success: true }
  }

  const updateSingle = async (mBoxId: number): Promise<void> => {
    const { data, error } = await motionViewApi.getSelectedBox(mBoxId)
    if (error) {
      //toastStore.showToast(ERROR, error)
    } else if (data) {
      const index = items.value.findIndex((item: MBox) => item.id === mBoxId)
      if (index > -1) {
        items.value[index].status = data.status
        items.value[index].flag = data.flag
        items.value[index].severity = data.severity
        items.value[index].isHidden = data.isHidden
      }
    }
  }

  const updateStatus = async (
    motionBoxId: number,
    status: MotionBoxStatusRequest,
  ): Promise<HttpResponse<Shipment | null, MotionStatusErrorCode>> => {
    return await motionViewApi.updateStatus(motionBoxId, status)
  }

  const updateDimensions = async (
    motionBoxId: number,
    dimensions: Record<number, number>,
  ): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> => {
    return await motionViewApi.updateDimensions(motionBoxId, dimensions)
  }

  const createNotification = async (
    motionBoxId: number,
    request: NotificationRequest,
  ): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> => {
    return await motionViewApi.createNotification(motionBoxId, request)
  }

  const getIndexOfItem = (id: number): MotionTrack => {
    const index = items.value.findIndex((item: MBox) => item.id === id)
    if (index === -1) return MotionTrackDefault
    return { id, index, status: items.value[index].status }
  }

  const getPreviousRelativeTo = (id: number | null): MotionTrack => {
    if (id === null) return MotionTrackDefault
    const index = items.value.findIndex((item: MBox) => item.id === id)
    if (index == 0) return MotionTrackDefault
    return { id: items.value[index - 1].id, index, status: items.value[index - 1].status }
  }

  const getNextRelativeTo = (id: number | null): MotionTrack => {
    if (id === null) return MotionTrackDefault
    const index = items.value.findIndex((item: MBox) => item.id === id) + 1
    if (index >= items.value.length) return MotionTrackDefault
    return { id: items.value[index].id, index, status: items.value[index].status }
  }

  const getNextFirstViewDateRelativeTo = (id: number | null): MotionTrack => {
    if (id === null) return MotionTrackDefault
    const baseIndex = items.value.findIndex((item: MBox) => item.id === id)
    let i = baseIndex
    if (items.value[i].viewDate && i < items.value.length - 1) i++ // were already at first item on day
    while (!items.value[i].viewDate) {
      i++
      if (i >= items.value.length) {
        return {
          id: items.value[baseIndex].id,
          index: baseIndex,
          status: items.value[baseIndex].status,
        }
      }
    }
    return { id: items.value[i].id, index: i, status: items.value[i].status }
  }

  const getPrevFirstViewDateRelativeTo = (id: number | null): MotionTrack => {
    if (id === null) return MotionTrackDefault
    const baseIndex = items.value.findIndex((item: MBox) => item.id === id)
    let i = baseIndex
    if (items.value[i].viewDate && i > 0) i-- // were already at first item on day
    while (!items.value[i].viewDate) {
      i--
      if (i < 0)
        return { id: items.value[baseIndex].id, index: baseIndex, status: items.value[i].status }
    }
    return { id: items.value[i].id, index: i, status: items.value[i].status }
  }

  const getIdsInRange = (fromId: number, toId: number): MotionTrack[] => {
    const range: MotionTrack[] = []
    let iStart = items.value.findIndex((item: MBox) => item.id === fromId)
    let iEnd = items.value.findIndex((item: MBox) => item.id === toId)
    if (iEnd < iStart) {
      const tmp = iEnd
      iEnd = iStart
      iStart = tmp
    }
    for (let i = iStart; i <= iEnd; i++) {
      const item = items.value[i]
      range.push({ id: item.id, index: i, status: item.status })
    }
    return range
  }

  const getAllIds = (): MotionTrack[] => {
    const mapped: MotionTrack[] = []
    items.value.forEach((item, index) => mapped.push({ id: item.id, index, status: item.status }))
    return mapped
  }

  const resetAll = () => {
    lastKnownChangeVersion.value = '0'
    trackId.value = 0
    items.value = []
    priorityItems.value = []
  }

  return {
    items,
    priorityItems,
    isLoading,
    statuses,
    loadStatuses,
    getDimensions,
    getExternalKey,
    getDeliveryNote,
    getStatusValidation,
    refreshMotionItems,
    updateMotionItems,
    updateStatus,
    updateDimensions,
    updateSingle,
    createNotification,
    resetAll,
    getIndexOfItem,
    getMotionSelectedItems,
    firstItem,
    getPreviousRelativeTo,
    getNextRelativeTo,
    getNextFirstViewDateRelativeTo,
    getPrevFirstViewDateRelativeTo,
    getAllIds,
    getIdsInRange,
  }
})
